import { CurrencyEnum, PrimaryButton, Tooltip, centsToUnits } from '@systemeio/ui'
import StripeWrapper from 'modules/profile/user-plan/components/price-plan-columns/components/stripe-wrapper'
import UpgradePlanConfirmationModal from 'modules/profile/user-plan/components/price-plan-columns/components/upgrade-plan-confirmation-modal'
import { BillingPeriodEnum } from 'modules/profile/user-plan/enums/billing-period-enum'
import { UserPlanTypeEnum } from 'modules/profile/user-plan/enums/user-plan-type-enum'
import {
  UseUserPlanInterface,
  UserPlanInterface,
} from 'modules/profile/user-plan/types/user-plan-interface'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { currencyFormat } from 'shared/utils/currency-format'
import { twMerge } from 'tailwind-merge'
import { calculatePlanParameters } from '../utils/calculate-plan-params'

export interface HeaderBlockProps {
  pricePlan: UserPlanInterface
  billingPeriod: BillingPeriodEnum
  active?: boolean
  isOneClickUpgradeAvailable?: boolean
  paymentMethod?: UseUserPlanInterface['customer']['paymentMethod']
  isPlanUpgradeAvailable?: boolean
  showWithTax: boolean
}

const HeaderBlock = ({
  pricePlan,
  billingPeriod,
  active = false,
  isOneClickUpgradeAvailable = false,
  paymentMethod,
  isPlanUpgradeAvailable,
  showWithTax,
}: HeaderBlockProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const { calculatedCost, calculatedCostWithTax, calculatedLink, calculatedPeriod } =
    calculatePlanParameters(pricePlan, billingPeriod)

  const isPriceInvisible =
    (pricePlan.type === UserPlanTypeEnum.promotion && !calculatedCost) || !pricePlan.currency

  let costTitleShort = ''
  let costTitle = ''

  if (pricePlan.currency) {
    costTitleShort = currencyFormat(
      centsToUnits(showWithTax ? calculatedCostWithTax : calculatedCost),
      pricePlan.currency || CurrencyEnum.EURO,
      user?.dashboardLocale,
      true,
    )
    costTitle = currencyFormat(
      centsToUnits(showWithTax ? calculatedCostWithTax : calculatedCost),
      pricePlan.currency || CurrencyEnum.EURO,
      user?.dashboardLocale,
    )
  }

  return (
    <div
      key={`headCell-${pricePlan.id}`}
      className={twMerge(
        'font-inter h-[150px] text-start  font-bold',
        active ? 'text-brand-primary' : 'text-text-primary',
        'mb-6 min-w-[100px]',
      )}
    >
      <div
        className={twMerge(
          'flex flex-col gap-2 border border-transparent px-2 py-2 lg:px-4',
          !active && 'border-fill-primary rounded-lg border-solid',
        )}
      >
        <div className="truncate text-xl font-medium">
          <Tooltip mode="overflow" label={pricePlan.title}>
            <span className="truncate" {...(active ? { ['data-test']: 'current-plan' } : {})}>
              {pricePlan.title}
            </span>
          </Tooltip>
        </div>
        <div className={twMerge('truncate', isPriceInvisible && 'invisible')}>
          <span className="text-xl lg:text-3xl">{costTitleShort}</span>
          <span className="text-base font-normal">/{t(calculatedPeriod).toLowerCase()}</span>
        </div>
        {active ? (
          <PrimaryButton
            as={LinkWithoutPrefetch}
            href={calculatedLink}
            title={t('dashboard.settings.user_plan.current_plan_title')}
            className="bg-fill-primary !text-text-primary border border-transparent px-1"
            width="small"
            aria-disabled
            testAttributePostfix={`button-plan-${pricePlan.title.toLowerCase()}-current`}
          >
            <span className="truncate">{t('dashboard.settings.user_plan.current_plan_title')}</span>
          </PrimaryButton>
        ) : isOneClickUpgradeAvailable ? (
          <StripeWrapper>
            <UpgradePlanConfirmationModal
              pricePlan={pricePlan}
              billingPeriod={billingPeriod}
              calculatedLink={calculatedLink}
              cost={costTitle + `/${t(calculatedPeriod).toLowerCase()}`}
              paymentMethod={paymentMethod}
              isPlanUpgradeAvailable={isPlanUpgradeAvailable}
            />
          </StripeWrapper>
        ) : (
          <div className="flex flex-col gap-2">
            <PrimaryButton
              as={LinkWithoutPrefetch}
              href={calculatedLink}
              title={t('dashboard.settings.user_plan.upgrade_plan_title')}
              className="bg-brand-primary hover:bg-background-secondary hover:text-brand-primary hover:border-brand-primary disabled:bg-text-secondary border-[1px] px-1 hover:border-solid"
              aria-disabled={!calculatedLink || !isPlanUpgradeAvailable}
              testAttributePostfix={`link-plan-${pricePlan.title.toLowerCase()}-upgrade`}
            >
              <span className="truncate">
                {t('dashboard.settings.user_plan.upgrade_plan_title')}
              </span>
            </PrimaryButton>
          </div>
        )}
        {active && user?.accessRestricted && (
          <div className="text-danger text-center text-base font-normal">
            {t('dashboard.settings.user_plan.restricted')}
          </div>
        )}
      </div>
    </div>
  )
}

export default HeaderBlock
